import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateWebSettings } from "@/api/onboarding/create-web-settings.ts";
import { useEffect } from "react";
import { useUpdateWebSettings } from "@/api/onboarding/update-web-settings.ts";
import useGlobalPermission from "@/store/user-permissions";

// Define the schema with Zod
const formSchema = z.object({
    shopify_api_key: z.string().min(1, "Shopify API Key is required"),
    shopify_access_token: z.string().optional(),
    shopify_api_secret: z.string().optional(),
    website_url: z.string().min(1, "Site URL is required"),
});

// Define form data type based on schema
type FormData = z.infer<typeof formSchema>;

type Props = {
    id: string | undefined | null;
    shopify_access_token: string | null | undefined;
    shopify_api_key: string | null | undefined;
    shopify_api_secret: string | null | undefined;
    website_url: string | null | undefined;
    mode?: string;
    locator?: string;
};

const ShopifyWebSettingsForm: React.FC<Props> = ({ id, shopify_api_secret, shopify_api_key, website_url, shopify_access_token, mode, locator }) => {
    // Initialize the form with react-hook-form and Zod resolver
    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            shopify_api_key: shopify_api_key || "",
            shopify_access_token: shopify_access_token || "",
            shopify_api_secret: shopify_api_secret || "",
            website_url: website_url || "",
        },
    });

    useEffect(() => {
        if (id) {
            form.setValue("shopify_api_secret", shopify_api_secret || "");
            form.setValue("shopify_api_key", shopify_api_key || "");
            form.setValue("shopify_access_token", shopify_access_token || "");
            form.setValue("website_url", website_url || "");
        }
    }, [id]);

    const { mutate: createWebSettings } = useCreateWebSettings();
    const { mutate: updateWebSettings } = useUpdateWebSettings();

    // Handle form submission
    const onSubmit = (data: FormData) => {
        if (!id) {
            createWebSettings({ ...data, website_type: "shopify", mode, locator });
            return;
        }
        updateWebSettings({ ...data, website_type: "shopify", mode, locator });
        return;
    };

    const { permissionList } = useGlobalPermission();
    const webPermission = permissionList.find((item) => item.module.name === "Web_Settings");

    useEffect(() => {
        if (!webPermission?.can_create || !webPermission?.can_update) {
            form.setValue("shopify_api_secret", "");
            form.setValue("shopify_api_key", "");
            form.setValue("shopify_access_token", "");
            form.setValue("website_url", "");
        }
    }, [webPermission, form]);

    return (
        <div className="my-4">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 grid grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="website_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Website URL *</FormLabel>
                                <FormControl>
                                    <Input
                                        disabled={!webPermission?.can_create || !webPermission?.can_update}
                                        className={"py-6"}
                                        placeholder="Enter Website URL"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="shopify_api_key"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Shopify API Key *</FormLabel>
                                <FormControl>
                                    <Input
                                        disabled={!webPermission?.can_create || !webPermission?.can_update}
                                        className={"py-6"}
                                        placeholder="Enter Shopify API key"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="shopify_access_token"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Shopify Access Token</FormLabel>
                                <FormControl>
                                    <Input
                                        disabled={!webPermission?.can_create || !webPermission?.can_update}
                                        className={"py-6"}
                                        placeholder="Enter Shopify access token"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="shopify_api_secret"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Shopify API Secret</FormLabel>
                                <FormControl>
                                    <Input
                                        disabled={!webPermission?.can_create || !webPermission?.can_update}
                                        className={"py-6"}
                                        placeholder="Enter Shopify API secret"
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className={"col-span-2"}>
                        <Button disabled={!webPermission?.can_create || !webPermission?.can_update} type="submit">
                            Save Configuration
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default ShopifyWebSettingsForm;
